/**
 * Blog Post 23: How Much Can You Borrow with a HK$10,000 Monthly Mortgage Payment?
 * 月供$10,000可以借幾多？按揭借貸能力解析
 */
import { useEffect } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import SEO from '../components/common/SEO';

const cellSx = { border: '1px solid #dbe4e2', p: 1, textAlign: 'right' as const };
const headSx = { ...cellSx, bgcolor: '#f1f5f4', fontWeight: 700 };

export default function BlogPost23() {
    const { i18n } = useCustomTranslation();
    const isEnglish = i18n.language === 'en';
    const slug = '/blog/how-much-can-you-borrow-hk10000-monthly-mortgage';

    useEffect(() => {
        trackPageView(slug, 'Blog Post 23');
        document.title = isEnglish
            ? 'How Much Can You Borrow with HK$10,000 a Month? Mortgage Affordability | PayWise365'
            : '月供$10,000可以借幾多？香港按揭借貸能力解析 | PayWise365';
    }, [isEnglish]);

    const seoTitle = isEnglish
        ? 'How Much Can You Borrow with HK$10,000 a Month? Mortgage Affordability Explained'
        : '月供$10,000可以借幾多？香港按揭借貸能力解析';
    const seoDescription = isEnglish
        ? 'Work backwards from a monthly payment to a loan amount. See how rate and term change borrowing power, and why banks also look at income, debts and stress tests.'
        : '由每月供款反推貸款額，了解利率與年期如何影響借貸能力，以及銀行為何同時考慮收入、負債及壓力測試。';

    const rows = [
        { rate: '3.0%', y20: '1.80M', y25: '2.11M', y30: '2.37M' },
        { rate: '3.5%', y20: '1.72M', y25: '2.00M', y30: '2.23M' },
        { rate: '4.0%', y20: '1.65M', y25: '1.90M', y30: '2.10M' },
        { rate: '5.5%', y20: '1.45M', y25: '1.63M', y30: '1.76M' },
    ];

    const table = (
        <Box sx={{ overflowX: 'auto', my: 2 }}>
            <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', minWidth: 420 }}>
                <thead>
                    <tr>
                        <Box component="th" sx={headSx}>{isEnglish ? 'Annual rate' : '年利率'}</Box>
                        <Box component="th" sx={headSx}>{isEnglish ? '20 years' : '20年'}</Box>
                        <Box component="th" sx={headSx}>{isEnglish ? '25 years' : '25年'}</Box>
                        <Box component="th" sx={headSx}>{isEnglish ? '30 years' : '30年'}</Box>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={row.rate}>
                            <Box component="td" sx={cellSx}>{row.rate}</Box>
                            <Box component="td" sx={cellSx}>{row.y20}</Box>
                            <Box component="td" sx={cellSx}>{row.y25}</Box>
                            <Box component="td" sx={cellSx}>{row.y30}</Box>
                        </tr>
                    ))}
                </tbody>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
            <SEO
                title={seoTitle}
                description={seoDescription}
                keywords={isEnglish
                    ? 'mortgage affordability Hong Kong, how much can I borrow, HK$10,000 monthly mortgage, loan amount from monthly payment, mortgage borrowing power, 按揭借貸能力'
                    : '按揭借貸能力, 月供計算, 可負擔樓價, 月供一萬借幾多, 香港按揭'}
                canonicalUrl={`https://www.paywise365.com${slug}`}
                ogType="article"
                ogImage="https://www.paywise365.com/logo.svg"
                publishDate="2026-10-07T00:00:00+08:00"
                modifiedDate="2026-10-07T00:00:00+08:00"
                author="PayWise365"
                articleSection={isEnglish ? 'Mortgage Guide' : '按揭指南'}
                tags={isEnglish
                    ? ['Mortgage', 'Affordability', 'Borrowing Power', 'Hong Kong Home Loan']
                    : ['按揭', '借貸能力', '可負擔樓價', '香港置業']}
            />
            <Box sx={{ bgcolor: '#155e63', color: 'white', py: { xs: 5, md: 7 } }}>
                <Container maxWidth="md">
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.85 }}>
                        <a href="/" style={{ color: 'white', textDecoration: 'none' }}>{isEnglish ? 'Home' : '首頁'}</a>
                        {' / '}
                        <a href="/blog" style={{ color: 'white', textDecoration: 'none' }}>{isEnglish ? 'Blog' : '資訊'}</a>
                        {' / '}
                        {isEnglish ? 'Mortgage Guide' : '按揭指南'}
                    </Typography>
                    <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
                        {isEnglish
                            ? 'How Much Can You Borrow with HK$10,000 a Month?'
                            : '月供$10,000可以借幾多？'}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 400, opacity: 0.92 }}>
                        {isEnglish
                            ? 'Work backwards from a monthly payment to a loan amount, then see what banks check on top.'
                            : '由每月供款反推貸款額，並了解銀行還會審查哪些條件。'}
                    </Typography>
                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
                <Box component="article" sx={{ color: '#334155', lineHeight: 1.85, '& h2': { mt: 4, mb: 1.5, color: '#123b40', fontWeight: 700 }, '& p': { mb: 2 } }}>
                    {isEnglish ? (
                        <>
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                                Mortgage Guide · Published 7 October 2026 · 6 min read
                            </Typography>
                            <Typography>
                                Many buyers start with a comfortable monthly payment rather than a property price. If you can set aside HK$10,000 a month for a mortgage, the loan amount that payment supports depends mainly on the interest rate and the repayment term. This article shows the arithmetic, and explains why the bank’s approved amount may be lower.
                            </Typography>

                            <Typography variant="h5">Working backwards from the payment</Typography>
                            <Typography>
                                A standard repayment mortgage is the present value of a series of equal monthly payments. Rearranging the usual payment formula gives:
                            </Typography>
                            <Box sx={{ bgcolor: '#f1f5f4', borderLeft: '4px solid #0f766e', p: 2.5, my: 2, fontFamily: 'monospace' }}>
                                <div>Loan = Payment × (1 − (1 + r)^−n) ÷ r</div>
                                <div>r = annual rate ÷ 12, n = years × 12</div>
                            </Box>
                            <Typography>
                                For example, a HK$10,000 monthly payment over 25 years at a constant 3.5% supports a loan of roughly HK$2.0 million. This is a simplified figure that assumes a fixed rate for the whole term and ignores fees, insurance and any rate changes.
                            </Typography>

                            <Typography variant="h5">Loan amount supported by HK$10,000 a month</Typography>
                            {table}
                            <Typography variant="body2" color="text.secondary">
                                Approximate loan amounts in HK$ millions, rounded, assuming a constant rate and equal monthly payments. Illustrations only, not bank quotations.
                            </Typography>
                            <Typography>
                                Two patterns stand out. Extending the term from 20 to 30 years at 3.5% raises the supported loan by about HK$0.5 million, but you pay interest for ten more years. And at the same 25-year term, moving from 3.5% to 5.5% cuts the supported loan by roughly HK$0.37 million. Payment size scales proportionally: HK$20,000 a month supports about double the loan of HK$10,000 under the same assumptions.
                            </Typography>

                            <Typography variant="h5">Why the bank may approve less</Typography>
                            <Typography component="div">
                                <ul>
                                    <li><strong>Income and debt-servicing ratio:</strong> Lenders usually compare all your monthly debt payments with your income. Car loans, credit cards, personal loans and other mortgages count.</li>
                                    <li><strong>Stress testing:</strong> Banks may test whether you could still afford the loan if rates were higher than today’s. Requirements follow regulatory guidance and bank policy and can change, so confirm the current rules.</li>
                                    <li><strong>Property value and loan-to-value limits:</strong> The loan is also capped by a percentage of the property’s valuation or price, which depends on the property type and your circumstances. Your down payment must cover the rest.</li>
                                    <li><strong>Floating-rate risk:</strong> If your loan is linked to HIBOR or Prime, today’s payment may not stay HK$10,000 for the full term.</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">What can raise your borrowing capacity?</Typography>
                            <Typography component="div">
                                <ul>
                                    <li>Reduce existing debts or credit limits before applying.</li>
                                    <li>Consider a longer term, keeping in mind it generally increases total interest.</li>
                                    <li>Compare lenders and rate structures, since a lower rate raises the loan a given payment can support.</li>
                                    <li>Apply with a co-borrower if your lender permits it and the household can meet the combined liability.</li>
                                    <li>Increase the down payment so the loan you need is smaller.</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">Borrowing the maximum is not the same as affordable</Typography>
                            <Typography>
                                A payment that fits the formula still has to fit your life. Leave room for management fees, rates, insurance, repairs and an emergency fund, and check how your budget copes if the payment rises. Many borrowers choose a loan comfortably below the maximum they are offered.
                            </Typography>

                            <Typography variant="h5">Try your own numbers</Typography>
                            <Typography>
                                Use the <a href="/calculators/loan-affordability" style={{ color: '#0f766e' }}>loan affordability calculator</a> to estimate a loan amount from your budget, then check the monthly figure with the <a href="/calculators/house-loan" style={{ color: '#0f766e' }}>house loan calculator</a>. To see how the balance falls over time, read our guide to <a href="/blog/hong-kong-mortgage-amortization-explained" style={{ color: '#0f766e' }}>mortgage amortization</a>. Confirm your actual limit, fees and terms with your lender.
                            </Typography>
                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #dbe4e2', fontSize: '0.9rem' }} color="text.secondary">
                                This article is for general educational purposes and is not personal financial or mortgage advice. Rates, fees, lending limits and loan terms vary by lender and can change.
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography color="text.secondary" variant="body2" sx={{ mb: 3 }}>
                                按揭指南 · 發佈日期：2026年10月7日 · 閱讀時間：6分鐘
                            </Typography>
                            <Typography>
                                不少買家會先想每月可負擔多少，再決定樓價。如果你每月可預留港幣10,000元供樓，這筆供款可支持的貸款額主要取決於利率及還款年期。本文介紹計算方法，並說明銀行批出的金額為何可能較低。
                            </Typography>

                            <Typography variant="h5">由供款反推貸款額</Typography>
                            <Typography>
                                標準還息還本按揭，等於一連串相同月供的現值。將一般供款公式重新整理，可得：
                            </Typography>
                            <Box sx={{ bgcolor: '#f1f5f4', borderLeft: '4px solid #0f766e', p: 2.5, my: 2, fontFamily: 'monospace' }}>
                                <div>貸款額 = 月供 × (1 − (1 + r)^−n) ÷ r</div>
                                <div>r = 年利率 ÷ 12，n = 年期 × 12</div>
                            </Box>
                            <Typography>
                                例如，月供港幣10,000元、還款期25年、全期利率維持3.5%，可支持的貸款額約為港幣200萬元。這是簡化數字，假設全期利率固定，並未計及費用、保險及利率變動。
                            </Typography>

                            <Typography variant="h5">月供$10,000可支持的貸款額</Typography>
                            {table}
                            <Typography variant="body2" color="text.secondary">
                                貸款額以港幣百萬元計並作四捨五入，假設利率不變及每月等額供款，只作示例，並非銀行報價。
                            </Typography>
                            <Typography>
                                從中可見兩個趨勢。利率3.5%時，年期由20年延長至30年，可支持貸款額增加約港幣50萬元，但需多付十年利息。在相同的25年年期下，利率由3.5%升至5.5%，可支持貸款額減少約港幣37萬元。月供與貸款額成正比：在相同假設下，月供港幣20,000元可支持的貸款額約為10,000元的兩倍。
                            </Typography>

                            <Typography variant="h5">銀行為何可能批較少？</Typography>
                            <Typography component="div">
                                <ul>
                                    <li><strong>收入與供款比率：</strong>銀行通常會將你所有每月債務供款與收入比較，汽車貸款、信用卡、私人貸款及其他按揭都會計算在內。</li>
                                    <li><strong>壓力測試：</strong>銀行可能評估利率高於現時水平時你是否仍能負擔。具體要求按監管指引及銀行政策而定，並可能調整，請向銀行查詢最新規定。</li>
                                    <li><strong>物業估值與按揭成數：</strong>貸款額亦受物業估值或成交價的一定百分比所限，視乎物業類型及個人情況而定，其餘部分須以首期支付。</li>
                                    <li><strong>浮息風險：</strong>若按揭與H按或P按掛鈎，現時的供款未必能在整個還款期維持港幣10,000元。</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">如何提升借貸能力？</Typography>
                            <Typography component="div">
                                <ul>
                                    <li>申請前減少現有債務或信用額度。</li>
                                    <li>考慮較長年期，但須留意一般會增加總利息。</li>
                                    <li>比較不同銀行及利率結構，利率越低，同一月供可支持的貸款額越高。</li>
                                    <li>如銀行允許，可與共同借款人一同申請，但須確保家庭能承擔合計的還款責任。</li>
                                    <li>增加首期，減少所需貸款額。</li>
                                </ul>
                            </Typography>

                            <Typography variant="h5">借到最高金額不等於負擔得起</Typography>
                            <Typography>
                                符合公式的供款，仍要配合你的生活。請預留管理費、差餉、保險、維修及緊急備用金，並檢視供款上升時預算能否應付。不少借款人會選擇明顯低於銀行所批最高額的貸款。
                            </Typography>

                            <Typography variant="h5">代入自己的數字</Typography>
                            <Typography>
                                你可使用<a href="/calculators/loan-affordability" style={{ color: '#0f766e' }}>貸款負擔能力計算機</a>，按預算估算貸款額，再以<a href="/calculators/house-loan" style={{ color: '#0f766e' }}>房屋貸款計算機</a>核對每月供款。想了解結餘如何逐步減少，可閱讀<a href="/blog/hong-kong-mortgage-amortization-explained" style={{ color: '#0f766e' }}>按揭攤還解析</a>。實際批核額、費用及條款，請向銀行確認。
                            </Typography>
                            <Typography sx={{ mt: 4, pt: 3, borderTop: '1px solid #dbe4e2', fontSize: '0.9rem' }} color="text.secondary">
                                本文只作一般教育用途，並非個人財務或按揭建議。利率、費用、借貸上限及貸款條款因銀行而異，亦可能有所變動。
                            </Typography>
                        </>
                    )}
                </Box>
            </Container>
        </Box>
    );
}
